/**
 * The search box writes `q` to the URL once typing pauses. Only a change to
 * the box starts the timer: a `q` that changes under it (Back, a tab switch)
 * must not be written back. The latest `update` and `q` are read through a
 * ref, so the timer that fires applies the text to the view as it is then.
 */

import { useEffect, useRef, useState } from 'react';

const SEARCH_DELAY_MS = 300;

export function useDebouncedSearch(q: string, update: (patch: { q: string }) => void) {
  const [search, setSearch] = useState(q);
  const latest = useRef({ q, update });
  useEffect(() => {
    latest.current = { q, update };
  });

  useEffect(() => {
    const text = search.trim();
    if (text === latest.current.q) return;
    const timer = window.setTimeout(() => latest.current.update({ q: text }), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  return [search, setSearch] as const;
}
